import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { faPlus, faTimes } from '@fortawesome/pro-light-svg-icons';

import { useStickyHeader } from '@/app/(main)/_components/search-modal/_hooks/use-sticky-header.hook';
import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import { Icon } from '@/components/ui/icon.component';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { CheckboxProcedure } from '@/app/(main)/_components/search-modal/_components/checkbox-procedure.component';
import { Button } from '@/components/ui/button/button.component';
import { MostSearchedResult } from '@/types/most-search-type';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { appendRecentSearches, useSearchState } from '@/store/search/search.store';
import { useRecentSearchesStorage } from '@/storage/recents-searches.storage';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { useLoading } from '@/hooks/use-loading.hook';
import { buildSearchResult } from '@/utils/search/search-result.util';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

interface SelectedOverviewExamsProps {
  selectedProcedures: SelectedProcedures[];
  locationOverride?: OverridedUserLocation;
  handleSelectProcedure: (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => void;
  onClear?: () => void;
}

export function SelectedOverviewExams({
  selectedProcedures,
  handleSelectProcedure,
  onClear,
  locationOverride,
}: SelectedOverviewExamsProps) {
  const router = useRouter();
  const [, setStoredRecentSearches] = useRecentSearchesStorage();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { handleSearchExamsUrl } = useSearch({ locationOverride });
  const { handleJump: jump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const { containerRef, headerCallbackRef, isScrolled } = useStickyHeader();

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch);
  const modalTitle = isVaccine ? 'Vacinas selecionadas' : 'Exames selecionados';
  const addMoreExamsButton = isVaccine ? 'Adicionar mais vacinas' : 'Adicionar mais exames';

  const isChecked = useCallback(
    (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => {
      return selectedProcedures.some((item) => item.id === procedure.id);
    },
    [selectedProcedures],
  );

  function handleJump(key: ProgressiveSearchModalKeys, returning: boolean = false) {
    if (jump) {
      jump({ key, returning });

      if (
        key === ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE
        || key === ProgressiveSearchModalKeys.SELECT_MORE_EXAMS
      ) {
        return;
      }

      if (onClear && returning) {
        onClear();
      }
    }
  }

  const handleNavigateToScheduleResult = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    startLoading();

    try {
      const searchResult = buildSearchResult(selectedProcedures, isVaccine);

      if (searchResult) {
        appendRecentSearches(searchResult, setStoredRecentSearches);
      }

      router.push(handleSearchExamsUrl(isVaccine, selectedProcedures));
    } catch (error) {
      const logger = getLogger('SearchModal - SelectedOverviewExams');
      logger.error('Error updating latest searches', { selectedProcedures, isVaccine }, error);
    } finally {
      stopLoading();
    }
  };

  const proceduresLabel = (): string => {
    const count = selectedProcedures.length;

    if (isVaccine) {
      return count === 1 ? '1 vacina adicionada' : `${count} vacinas adicionadas`;
    }

    return count === 1 ? '1 exame adicionado' : `${count} exames adicionados`;
  };

  return (
    <div className="h-full flex flex-col justify-between py-5 px-6">
      <TrackUserView eventName={AnalyticsEnum.VIEW_SELECTED_OVERVIEW_EXAMS} />

      <SheetHeader
        ref={headerCallbackRef}
        className={`
          sticky top-0 left-0 right-0 z-20
          -mx-6 -mt-5
          px-6 pt-5
          transition-colors duration-200
          ${isScrolled ? 'bg-gray-50 shadow-floating pb-3' : 'bg-white'}
        `}
      >
        <div className="flex justify-between gap-2.5 items-start md:items-center">
          <div className="flex gap-4 items-start md:items-center">
            <SheetTitle>
              <div className="flex gap-2.5">
                <Typography
                  variant={TypographyVariant.MODAL_TITLE}
                  className="text-wrap text-left"
                  data-testid="selected-overview-exams-title"
                >
                  {modalTitle}
                </Typography>
              </div>
            </SheetTitle>
          </div>
          <button
            className="text-gray-500 cursor-pointer self-start md:self-center mt-2 md:mt-0 hover:bg-gray-50"
            data-testid="back--selected-overview-exams"
            onClick={() => {
              if (selectedProcedures.length === 0) {
                handleJump(ProgressiveSearchModalKeys.SEARCH, true);
                return;
              }

              handleJump(ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, true);
            }}
          >
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </button>
        </div>

        <Typography className="font-semibold !mt-4">
          {proceduresLabel()}
        </Typography>
      </SheetHeader>

      <div
        ref={containerRef}
        className="overflow-y-auto h-full md:basis-[40vh] -mx-6"
      >
        {selectedProcedures.map((procedure) => (
          <CheckboxProcedure
            key={procedure.id || procedure.code}
            procedure={procedure}
            handleSelectProcedure={handleSelectProcedure}
            isChecked={isChecked}
          />
        ))}
      </div>

      <div className="space-y-4 shadow-modal p-5 -mx-6 -mb-5">
        <Button
          data-testid="see-schedules--selected-overview-exams"
          isLoading={isLoading}
          disabled={selectedProcedures.length === 0}
          onClick={handleNavigateToScheduleResult}
        >
          Ver horários disponíveis
        </Button>
        <Button
          data-testid="add-more--selected-overview-exams"
          disabled={isLoading}
          variant="secondary"
          startIcon={faPlus}
          onClick={() => handleJump(ProgressiveSearchModalKeys.SELECT_MORE_EXAMS, true)}
        >
          {addMoreExamsButton}
        </Button>
      </div>
    </div>
  );
}
